import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router'
import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'

export interface BreadcrumbItem {
  label: string
  /** Udelades på det sidste led, som repræsenterer den aktuelle side. */
  to?: string
}

export function Breadcrumbs({
  items,
  className,
}: {
  items: BreadcrumbItem[]
  className?: string
}) {
  if (items.length === 0) return null

  return (
    <nav aria-label="Brødkrummer" className={cn('min-w-0', className)}>
      <ol className="flex items-center gap-1 text-[13px] text-text-muted">
        {items.map((item, index) => {
          const isLast = index === items.length - 1

          return (
            <li key={`${item.label}-${index}`} className="flex min-w-0 items-center gap-1">
              {index > 0 && (
                <ChevronRight className="size-3.5 shrink-0 opacity-60" aria-hidden="true" />
              )}

              {isLast || !item.to ? (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  className={cn('truncate', isLast && 'text-text-secondary')}
                >
                  {item.label}
                </span>
              ) : (
                <Link
                  to={item.to}
                  className="truncate rounded transition-colors hover:text-text-secondary focus-visible:outline-2 focus-visible:outline-brand-400"
                >
                  {item.label}
                </Link>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

export interface PageHeaderProps {
  title: ReactNode
  description?: ReactNode
  /** Vises til venstre for titlen, fx et projektikon. */
  leading?: ReactNode
  /** Vises under titlen, fx statusbadges og deltagere. */
  meta?: ReactNode
  actions?: ReactNode
  className?: string
}

export function PageHeader({
  title,
  description,
  leading,
  meta,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('flex flex-wrap items-start justify-between gap-4', className)}>
      <div className="flex min-w-0 items-start gap-3">
        {leading}

        <div className="min-w-0">
          <h1 className="truncate text-2xl leading-tight font-semibold tracking-tight">{title}</h1>

          {description && (
            <p className="mt-0.5 text-[13px] text-text-muted">{description}</p>
          )}

          {meta && <div className="mt-2 flex flex-wrap items-center gap-2">{meta}</div>}
        </div>
      </div>

      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
  )
}
